import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useSidebar } from '@/_components/sidebar';
import { CardContainer } from '@/_containers/layout';
import { useScreen } from '@/_shared';

import { NotesList } from './notes-list';

import { invalidateNotesQuery, useCreateNoteMutation, useGetNotes } from '../shared/apis';
import { NotesForm } from '../shared/components/notes-form';
import type { NoteMutationSchema } from '../shared/schemas/notes-mutation-schema';
import type { NoteContextTypeSchema } from '../shared/schemas/notes-query-schema';

type NotesListPageProps = {
  contextType: NoteContextTypeSchema;
  contextId: string;
  title?: string;
  showForm?: boolean;
  maxMessageLength?: number;
};

export default function NotesListPage(props: NotesListPageProps) {
  const { contextType, contextId, showForm = true, maxMessageLength } = props;
  const { state, isMobile } = useSidebar();
  const { data: notesResponse, isLoading } = useGetNotes(contextType, contextId);
  const createNoteMutation = useCreateNoteMutation();
  const { width } = useScreen();

  const articleRef = useRef<HTMLElement | null>(null);
  const fixedSectionRef = useRef<HTMLElement | null>(null);
  const [fixedDims, setFixedDims] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [fixedHeight, setFixedHeight] = useState<number>(0);

  // Recalculate fixed section alignment to follow the article width/position
  const recalcFixed = () => {
    const el = articleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Use viewport-based left/width so a fixed element can align perfectly
    setFixedDims({ left: Math.round(rect.left), width: Math.round(rect.width) });
  };

  useLayoutEffect(() => {
    recalcFixed();
  }, []);

  useEffect(() => {
    // Update on viewport resize and when sidebar state toggles/collapses
    recalcFixed();
  }, [width, state, isMobile]);

  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => recalcFixed());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track the dynamic height of the fixed NotesForm section
  useLayoutEffect(() => {
    const measure = () => {
      const el = fixedSectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setFixedHeight(Math.round(rect.height));
    };
    measure();
  }, [width, state, isMobile]);

  useEffect(() => {
    const el = fixedSectionRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setFixedHeight(Math.round(rect.height));
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scrollToBottom = () => {
    // Scroll document body to bottom with smooth behavior
    window.scrollTo({
      top: document.body.scrollHeight + 100,
      behavior: 'smooth',
    });
  };

  const handleCreateNote = async (data: NoteMutationSchema) => {
    await createNoteMutation.mutateAsync(data, {
      onSuccess: async () => {
        await invalidateNotesQuery(contextType, contextId);
        setTimeout(() => {
          scrollToBottom();
        }, 250);
      },
    });
  };

  const notes = notesResponse?.data || [];

  useEffect(() => {
    scrollToBottom();
  }, []);

  return (
    <article ref={articleRef}>
      {/** Keep content visible above the fixed footer by adding a dynamic bottom margin.
       * Base is Tailwind mb-40 (10rem ≈ 160px). If the fixed section is taller, use that instead.
       */}
      <CardContainer
        bodyClassName="3xl:px-6 px-6"
        style={{ marginBottom: (showForm ? Math.max(160, fixedHeight) - 20 : 10) + 'px' }}
      >
        <NotesList
          notes={notes}
          contextType={contextType}
          contextId={contextId}
          isLoading={isLoading}
          maxMessageLength={maxMessageLength}
        />
      </CardContainer>

      {showForm && (
        <section
          ref={fixedSectionRef}
          className="fixed bottom-0 transform-gpu"
          style={{ left: fixedDims.left, width: fixedDims.width }}
        >
          <CardContainer
            bodyClassName="3xl:px-6 px-6 py-6"
            className="border-c-border shadow-up rounded-b-none border border-b-0"
          >
            <NotesForm onSubmit={handleCreateNote} contextType={contextType} contextId={contextId} />
          </CardContainer>
        </section>
      )}
    </article>
  );
}
